import { ChevronUpIcon } from "@chakra-ui/icons";
import {
  Box,
  Divider,
  VStack,
  AccordionItem,
  AccordionButton,
  AccordionIcon,
  AccordionPanel,
  Text,
} from "@chakra-ui/react";
import React, { useMemo } from "react";
import { CherryHubAdminApi } from "../../api/adminApiModels";
import { ChakraReactSelectField } from "../fields/SelectField/ChakraReactSelectField";
import { SwitchField } from "../fields/SwitchField/SwitchField";
import { TimePeriod } from "./RegistrationFormTimePeriod";
import { Au10TixWorkflowTypeList } from "../../enums/Au10TixWorkflowType";
import {
  CustomerRiskManagementThirdParty,
  CustomerRiskManagementThirdPartyList,
} from "../../enums/CustomerRiskManagementThirdParty";
import { TextField } from "../fields/TextField/TextField";

interface CustomerRiskManagementFlowSectionProps {
  customerRiskManagementFlowEnabled: boolean | undefined;
  customerRiskManagementFlowConfiguration:
    | CherryHubAdminApi.CustomerRiskManagementFlowConfiguration
    | undefined;
}

export const RegistrationFormCustomerRiskManagementFlowSection = ({
  customerRiskManagementFlowEnabled,
  customerRiskManagementFlowConfiguration,
}: CustomerRiskManagementFlowSectionProps) => {
  const customerRiskManagementFlowConfigurationView = useMemo(() => {
    return (
      <Box
        w="100%"
        p={4}
        border="1px solid"
        borderColor="cherryButtonDark.50"
        borderRadius="md"
      >
        <Text fontSize="sm">
          <b>Customer Risk Management Flow Configuration</b>
        </Text>
        <Divider mb={2} />

        <VStack w="100%" alignItems="flex-start">
          <ChakraReactSelectField
            horizontal
            label="Risk Management Provider"
            name="customerRiskManagementFlowConfiguration.ThirdParty"
            selectedOptionColor="blue"
            options={CustomerRiskManagementThirdPartyList()}
            className="relative-listbox"
          />
          {customerRiskManagementFlowConfiguration?.ThirdParty ===
            CustomerRiskManagementThirdParty.Bndry && (
            <TextField
              horizontal
              label="Onboard Individual Entity Job Id"
              name="customerRiskManagementFlowConfiguration.BndryCustomerThirdPartyWorkflowConfiguration.OnboardIndividualEntityJobId"
            />
          )}
          {customerRiskManagementFlowConfiguration?.ThirdParty ===
            CustomerRiskManagementThirdParty.Au10Tix && (
            <ChakraReactSelectField
              horizontal
              label="Workflow Type"
              name="customerRiskManagementFlowConfiguration.Au10TixCustomerThirdPartyWorkflowConfiguration.WorkflowType"
              selectedOptionColor="blue"
              options={Au10TixWorkflowTypeList()}
              className="relative-listbox"
            />
          )}
          <TimePeriod
            headerLabel="Workflow TTL"
            fieldName="customerRiskManagementFlowConfiguration.WorkflowTimeToLive"
            borderColor="cherryButtonDark.100"
            timePeriod={
              customerRiskManagementFlowConfiguration?.WorkflowTimeToLive
            }
            isRelativeListbox
          />
          <SwitchField
            horizontal
            label="Full page redirect"
            name="customerRiskManagementFlowConfiguration.UseFullPageRedirect"
            infoText="When on, the applicant leaves the form and completes verification as a full page, then returns to Cherry Hub. When off, verification stays embedded in the form. Full-page redirect only works if the provider can send the applicant back to Cherry Hub."
          />
        </VStack>
      </Box>
    );
  }, [customerRiskManagementFlowConfiguration]);

  const customerRiskManagementFlowEnabledView = useMemo(() => {
    return (
      <AccordionItem>
        {({ isExpanded }) => (
          <>
            <h2>
              <AccordionButton>
                <Box flex="1" textAlign="left">
                  <b>Customer Risk Management Flow</b>
                </Box>
                {isExpanded ? (
                  <ChevronUpIcon color="white" fontSize="xl" />
                ) : (
                  <AccordionIcon />
                )}
              </AccordionButton>
            </h2>
            <AccordionPanel width="100%">
              <VStack w="100%" alignItems="flex-start">
                <SwitchField
                  horizontal
                  label="Flow Enabled"
                  name="customerRiskManagementFlowEnabled"
                />
                {customerRiskManagementFlowEnabled &&
                  customerRiskManagementFlowConfigurationView}
              </VStack>
            </AccordionPanel>
          </>
        )}
      </AccordionItem>
    );
  }, [
    customerRiskManagementFlowEnabled,
    customerRiskManagementFlowConfiguration,
  ]);

  return <>{customerRiskManagementFlowEnabledView}</>;
};
